// Custom element wiring the Northbound community selector to the headless
// Northbound app. Owns: dialog open/close, populating <select> from bootstrap
// communities (grouped by region), trigger label updates, and the single
// `setCommunity` handoff on Apply. Never writes localStorage directly — the
// app owns `northbound.selectedCommunityKey`.

const REGION_LABELS = {
  QIKIQTAALUK: 'Qikiqtaaluk',
  KIVALLIQ: 'Kivalliq',
  KITIKMEOT: 'Kitikmeot',
};
const ADD_TO_CART_SELECTOR = '[data-northbound-add-to-cart]';
const ADD_TO_CART_QUEUE_TIMEOUT_MS = 60000;
const PRICE_DISPLAY_SELECTOR = '[data-northbound-price-display]';
const PLACEHOLDER_PRICE_LABELS = new Set(['Select community', 'Select community for price']);

function regionLabel(region) {
  if (region && REGION_LABELS[region]) return REGION_LABELS[region];
  if (!region) return 'Other';
  return region
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getStorefront() {
  return window.NorthboundStorefront || null;
}

function getCommunities() {
  const sf = getStorefront();
  const bootstrap = sf?.getBootstrap?.();
  return Array.isArray(bootstrap?.communities) ? bootstrap.communities : [];
}

function getSelectedCommunity() {
  return getStorefront()?.getSelectedCommunity?.() || null;
}

class NorthboundCommunitySelector extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.select = null;
    this.applyButton = null;
    this.closeButton = null;
    this.titleElement = null;
    this.bootstrapReady = false;
    this.hasPopulated = false;
    this.lastCommunityKey = null;
    this.staleCommunityKey = null;
    this.pendingAdd = null;
    this.pendingAddTimer = null;

    this.onAddToCartClick = this.onAddToCartClick.bind(this);
    this.onAddToCartSubmit = this.onAddToCartSubmit.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onBootstrapReady = this.onBootstrapReady.bind(this);
    this.onOpenRequested = this.onOpenRequested.bind(this);
    this.onCommunityChanged = this.onCommunityChanged.bind(this);
    this.onApplyClick = this.onApplyClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onPricingApplied = this.onPricingApplied.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.select = this.querySelector('[data-northbound-community-select]');
    this.applyButton = this.querySelector('[data-northbound-community-apply]');
    this.closeButton = this.querySelector('[data-northbound-community-close]');
    this.titleElement = this.querySelector('#NorthboundCommunityDialog-title');

    document.addEventListener('click', this.onAddToCartClick, true);
    document.addEventListener('submit', this.onAddToCartSubmit, true);
    document.addEventListener('click', this.onTriggerClick);
    window.addEventListener('northbound:bootstrap-ready', this.onBootstrapReady);
    window.addEventListener('northbound:open-selector-requested', this.onOpenRequested);
    window.addEventListener('northbound:community-changed', this.onCommunityChanged);
    window.addEventListener('northbound:cart-pricing-applied', this.onPricingApplied);
    this.applyButton?.addEventListener('click', this.onApplyClick);
    this.closeButton?.addEventListener('click', this.onCloseClick);
    this.dialog?.addEventListener('close', this.onDialogClose);

    if (getCommunities().length) this.populateSelect();
    this.lastCommunityKey = getSelectedCommunity()?.communityKey || null;
    this.updateTriggerLabels();
    this.schedulePriceHydrationCleanup();
  }

  disconnectedCallback() {
    this.clearPendingAdd();
    document.removeEventListener('click', this.onAddToCartClick, true);
    document.removeEventListener('submit', this.onAddToCartSubmit, true);
    document.removeEventListener('click', this.onTriggerClick);
    window.removeEventListener('northbound:bootstrap-ready', this.onBootstrapReady);
    window.removeEventListener('northbound:open-selector-requested', this.onOpenRequested);
    window.removeEventListener('northbound:community-changed', this.onCommunityChanged);
    window.removeEventListener('northbound:cart-pricing-applied', this.onPricingApplied);
    this.applyButton?.removeEventListener('click', this.onApplyClick);
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.dialog?.removeEventListener('close', this.onDialogClose);
  }

  onAddToCartClick(event) {
    const control = event.target.closest(ADD_TO_CART_SELECTOR);
    if (!control || !this.shouldCaptureAddToCart(control)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.queueAdd(control, () => {
      control.click();
    });
  }

  onAddToCartSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    const control = submitter?.matches(ADD_TO_CART_SELECTOR)
      ? submitter
      : form.querySelector(ADD_TO_CART_SELECTOR);
    if (!control || !this.shouldCaptureAddToCart(control)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.queueAdd(control, () => {
      if (typeof form.requestSubmit === 'function') {
        if (submitter) {
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
        return;
      }
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  onTriggerClick(event) {
    const trigger = event.target.closest('[data-northbound-trigger]');
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  onBootstrapReady(event) {
    this.bootstrapReady = true;
    this.populateSelect();
    this.lastCommunityKey = getSelectedCommunity()?.communityKey || this.lastCommunityKey;
    this.updateTriggerLabels();
    const shouldAutoOpen = !!event?.detail?.shouldAutoOpenSelector;
    const hasSelection = !!document.documentElement.getAttribute('data-northbound-community-key');
    if (!hasSelection) this.clearPriceLoadingState();
    if (shouldAutoOpen && !hasSelection) this.open();
  }

  onOpenRequested(event) {
    this.open(event?.detail?.context || 'default');
  }

  onCommunityChanged() {
    const currentKey = getSelectedCommunity()?.communityKey || null;
    const switchedCommunity = Boolean(this.lastCommunityKey && currentKey && this.lastCommunityKey !== currentKey);
    this.lastCommunityKey = currentKey;

    this.updateTriggerLabels();
    if (this.hasPopulated) this.populateSelect();
    if (switchedCommunity) {
      this.markPriceDisplaysStale();
    } else {
      this.schedulePriceHydrationCleanup();
    }
    if (this.pendingAdd) {
      this.replayPendingAdd();
    } else if (getSelectedCommunity() && this.dialog?.open) {
      this.close();
    }
  }

  onPricingApplied(event) {
    const appliedCommunityKey = event?.detail?.communityKey || null;
    this.schedulePriceHydrationCleanup({
      clearStale: Boolean(appliedCommunityKey && appliedCommunityKey === this.staleCommunityKey),
    });
  }

  onApplyClick(event) {
    event.preventDefault();
    const key = this.select?.value;
    if (!key) return;
    const sf = getStorefront();
    sf?.setCommunity?.(key);
    this.close();
  }

  onCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  onDialogClose() {
    if (this.pendingAdd && !getSelectedCommunity()) this.clearPendingAdd();
    this.setDialogContext('default');
  }

  open(context = 'default') {
    if (!this.dialog) return;
    this.setDialogContext(context);
    if (!this.hasPopulated) this.populateSelect();
    if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  queueAdd(control, replay) {
    if (!(control instanceof HTMLElement) || typeof replay !== 'function') return;

    this.clearAddPrompts();
    this.clearPendingAdd();
    this.pendingAdd = { control, replay };
    this.pendingAddTimer = window.setTimeout(() => {
      this.clearPendingAdd();
    }, ADD_TO_CART_QUEUE_TIMEOUT_MS);
    this.open('atc');
  }

  replayPendingAdd() {
    const pending = this.pendingAdd;
    if (!pending || !getSelectedCommunity()) return;
    this.clearPendingAdd();
    this.close();
    window.setTimeout(() => {
      if (pending.control.isConnected) pending.replay();
    }, 0);
  }

  clearPendingAdd() {
    if (this.pendingAddTimer) {
      window.clearTimeout(this.pendingAddTimer);
      this.pendingAddTimer = null;
    }
    this.pendingAdd = null;
  }

  shouldCaptureAddToCart(control) {
    if (!(control instanceof HTMLElement)) return false;
    if (getSelectedCommunity()) return false;
    if (control.disabled || control.getAttribute('aria-disabled') === 'true') return false;
    if (control.dataset.northboundPricingState === 'unavailable') return false;
    if (control.closest('modal-opener') || control.getAttribute('aria-haspopup') === 'dialog') return false;
    return true;
  }

  setDialogContext(context) {
    const isAtc = context === 'atc';
    this.dataset.context = isAtc ? 'atc' : 'default';
    if (this.dialog) this.dialog.dataset.context = this.dataset.context;
    if (this.titleElement) {
      this.titleElement.textContent = isAtc
        ? this.dataset.titleAtc || this.dataset.titleDefault || this.titleElement.textContent
        : this.dataset.titleDefault || this.titleElement.textContent;
    }
    if (this.applyButton) {
      this.applyButton.textContent = isAtc
        ? this.dataset.applyAtc || this.dataset.applyDefault || this.applyButton.textContent
        : this.dataset.applyDefault || this.applyButton.textContent;
    }
  }

  clearAddPrompts(scope) {
    const root = scope instanceof Element ? scope.parentElement || document : document;
    root.querySelectorAll('[data-northbound-atc-prompt]').forEach((prompt) => {
      prompt.remove();
    });
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function' && this.dialog.open) {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  }

  populateSelect() {
    if (!this.select) return;
    const communities = getCommunities();
    if (!communities.length) {
      this.select.innerHTML = `<option value="">${this.dataset.emptyLabel || 'No communities available'}</option>`;
      return;
    }

    const current = getSelectedCommunity();
    const currentKey = current?.communityKey || '';

    const groups = new Map();
    for (const c of communities) {
      const key = c.region || 'OTHER';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }

    const placeholder = this.dataset.placeholder || 'Select a community…';
    const fragment = document.createDocumentFragment();

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    fragment.appendChild(placeholderOption);

    for (const [region, list] of groups) {
      const optgroup = document.createElement('optgroup');
      optgroup.label = regionLabel(region);
      for (const c of list) {
        const option = document.createElement('option');
        option.value = c.communityKey;
        option.textContent = c.name;
        if (c.communityKey === currentKey) option.selected = true;
        optgroup.appendChild(option);
      }
      fragment.appendChild(optgroup);
    }

    this.select.replaceChildren(fragment);
    this.hasPopulated = true;
  }

  updateTriggerLabels() {
    const labels = document.querySelectorAll('[data-northbound-trigger-label]');
    if (!labels.length) return;
    const current = getSelectedCommunity();
    const firstLabel = labels[0];
    const fallback = firstLabel.dataset.defaultLabel || 'Choose location';
    const loading = firstLabel.dataset.loadingLabel || 'Community';
    const text = current?.name || (this.bootstrapReady ? fallback : loading);
    labels.forEach((el) => {
      el.textContent = text;
    });
  }

  schedulePriceHydrationCleanup(options = {}) {
    this.cleanupHydratedPrices(options);
    window.setTimeout(() => this.cleanupHydratedPrices(options), 100);
    window.setTimeout(() => this.cleanupHydratedPrices(options), 500);
  }

  cleanupHydratedPrices(options = {}) {
    let hasHydratedPrice = false;
    let hasStalePrice = false;
    document.querySelectorAll(PRICE_DISPLAY_SELECTOR).forEach((node) => {
      const text = String(node.textContent || '').trim();
      if (!this.isHydratedPriceText(text)) return;

      const staleText = node.dataset.northboundStalePriceText || '';
      const stillWaitingForFreshPrice =
        node.hasAttribute('data-northbound-price-stale') &&
        staleText &&
        text === staleText &&
        !options.clearStale;

      if (stillWaitingForFreshPrice) {
        hasStalePrice = true;
        return;
      }

      node.removeAttribute('data-northbound-price-placeholder');
      node.removeAttribute('data-northbound-price-stale');
      delete node.dataset.northboundStalePriceText;
      hasHydratedPrice = true;
    });

    if (hasHydratedPrice && !hasStalePrice) {
      this.staleCommunityKey = null;
      this.clearPriceLoadingState();
    }
  }

  markPriceDisplaysStale() {
    let hasStalePrice = false;

    document.querySelectorAll(PRICE_DISPLAY_SELECTOR).forEach((node) => {
      const text = String(node.textContent || '').trim();
      if (!this.isHydratedPriceText(text)) return;
      node.setAttribute('data-northbound-price-stale', '');
      node.dataset.northboundStalePriceText = text;
      hasStalePrice = true;
    });

    if (hasStalePrice) {
      this.staleCommunityKey = getSelectedCommunity()?.communityKey || null;
      document.documentElement.setAttribute('data-northbound-unit-pricing-loading', 'true');
    }
  }

  isHydratedPriceText(text) {
    if (!text || PLACEHOLDER_PRICE_LABELS.has(text)) return false;
    return text !== 'Pricing unavailable';
  }

  clearPriceLoadingState() {
    document.documentElement.removeAttribute('data-northbound-community-pending');
    document.documentElement.removeAttribute('data-northbound-unit-pricing-loading');
  }
}

customElements.define('northbound-community-selector', NorthboundCommunitySelector);
